import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Text from "@galaxy-io/dls/text/Text";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import { workflows } from "@gtm/contracts/workflows/catalog";
import { isDeliveryWorkflow } from "@gtm/contracts/workflows/providers";
import { useState } from "react";
import { useRouter, useUrlState } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { apiClient, unwrap } from "@/lib/api-client";
import { sourceLabel } from "@/lib/source-label";
import { toastDefaults } from "@/lib/toast";
import { GitHubWorkflowRunDialog } from "./github-workflow-run-dialog";
export function RunControls({
  id,
  running,
  workflowKey,
  sourceId,
  sourceName,
  onComplete,
}: {
  id: string;
  running: boolean;
  workflowKey: string;
  sourceId: string | null;
  sourceName: string;
  onComplete: () => void;
}) {
  const [pending, setPending] = useState(false);
  const [terminateOpen, setTerminateOpen] = useState(false);
  const { params, update } = useUrlState();
  const launchOpen = params.get("runAgain") === id;
  const setLaunchOpen = (open: boolean) => update({ runAgain: open ? id : null });
  const workflow = workflows.find((entry) => entry.key === workflowKey);
  const { toast } = useToast();
  const router = useRouter();
  async function perform(action: "cancel" | "terminate" | "retry") {
    setPending(true);
    try {
      const result = await unwrap(
        apiClient().POST("/workflows/runs/actions", {
          body: { workflowIds: [id], action },
        }),
      );
      if (result.failed.length) throw new Error(result.failed[0].error);
      toast({
        variant: ToastVariant.SUCCESS,
        header:
          action === "cancel"
            ? "Cancellation requested"
            : action === "terminate"
              ? "Run terminated"
              : "Workflow started",
        description:
          action === "cancel"
            ? "The run stops once the current step finishes."
            : action === "terminate"
              ? "Temporal has stopped the run."
              : "Temporal starts the run shortly.",
        ...toastDefaults(ToastVariant.SUCCESS),
      });
      setLaunchOpen(false);
      setTerminateOpen(false);
      onComplete();
      router.refresh();
    } catch (error) {
      if (action === "terminate") throw error;
      toast({
        variant: ToastVariant.ERROR,
        header: action === "retry" ? "Could not start run" : `Could not ${action} run`,
        description: (error as Error).message,
        ...toastDefaults(ToastVariant.ERROR),
      });
    } finally {
      setPending(false);
    }
  }
  return (
    <>
      {workflow &&
      workflow.key !== "github.enrich_person" &&
      sourceId &&
      !isDeliveryWorkflow(workflowKey) ? (
        <GitHubWorkflowRunDialog
          open={launchOpen}
          workflow={workflow}
          repositories={[{ id: sourceId, name: sourceName, provider: workflowKey.split(".")[0] }]}
          sourceId={sourceId}
          onSourceChange={() => {}}
          onClose={() => {
            setLaunchOpen(false);
            onComplete();
          }}
        />
      ) : (
        <SurfaceDialog
          open={launchOpen}
          title="Run again"
          busy={pending}
          onClose={() => setLaunchOpen(false)}
          footer={
            <Button
              label="Run workflow"
              isLoading={pending}
              isDisabled={pending}
              onClick={() => perform("retry")}
            />
          }
        >
          <Text>
            Start another run of {workflow?.name || workflowKey} for{" "}
            {sourceLabel(workflowKey.split(".")[0], sourceName)}?
          </Text>
        </SurfaceDialog>
      )}
      <ConfirmAction
        isOpen={terminateOpen && running}
        onOpenChange={setTerminateOpen}
        header="Terminate run?"
        description="Stop this run immediately without waiting for cleanup. Work already completed is preserved. Use this when graceful cancellation is not stopping the run."
        label="Terminate run"
        isDestructive
        onConfirm={() => perform("terminate")}
      />
      {running && (
        <Button
          label="Terminate run"
          variant={ButtonVariant.TERTIARY}
          isDisabled={pending}
          onClick={() => setTerminateOpen(true)}
        />
      )}
      <Button
        label={running ? "Cancel run" : "Run again"}
        variant={running ? ButtonVariant.SECONDARY : ButtonVariant.PRIMARY}
        isLoading={pending}
        isDisabled={pending}
        onClick={running ? () => perform("cancel") : () => setLaunchOpen(true)}
      />
    </>
  );
}
